/**
 * Group Leaderboard Screen
 * Stack screen under Friends tab – back pops (no reload).
 */

import { useEffect, useMemo, useState, useCallback } from 'react'
import { View, Text, ActivityIndicator, Pressable, Platform, TextInput } from 'react-native'
import { Image } from 'expo-image'
import { WEB_HOVER_TRANSITION_STYLE } from '../../../../src/shared/constants/animations'
import { Redirect, useLocalSearchParams, useRouter } from 'expo-router'
import { LinearGradient } from 'expo-linear-gradient'
import { ArrowLeft, Check, Pencil } from 'lucide-react-native'
import { DEFAULT_GROUP_IMAGE_SOURCE } from '../../../../constants/assets'
import { webImageAltGroupCover } from '../../../../src/shared/helpers/imageWebAlt.helper'
import { useSupabaseAuth } from '../../../../src/shared/contexts/SupabaseAuthContext'
import { useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from '../../../../src/shared/hooks/useSnackbar'
import { useGroupNameEdit } from '../../../../src/friends/hooks/useGroupNameEdit'
import { useGroupMemberActions } from '../../../../src/friends/hooks/useGroupMemberActions'
import { useAddFriendFromMenu } from '../../../../src/friends/hooks/useAddFriendFromMenu'
import { PageHeader } from '../../../../components/layout'
import { InviteFriendsButton } from '../../../../components/friends/InviteFriendsButton'
import { GroupInvitationModal } from '../../../../components/groups/GroupInvitationModal/GroupInvitationModal'
import { GroupLeaderboardList } from '../../../../components/friends/GroupLeaderboardList'
import { buildFriendStatusByUserId } from '../../../../src/friends/helpers/friendStatusMap.helper'
import { dashboardToGroup } from '../../../../src/friends/helpers/groupDashboard.helper'
import { getFriendsPageStyles } from '../../../../src/friends/styles/friends.styles'
import {
  getGroupLeaderboardStyles,
  getGroupTitleIconMarginLeft,
} from '../../../../appStyles/_groupLeaderboard.styles'
import { useTheme } from '../../../../src/theme/useTheme'
import { useAvatarContext } from '../../../../src/avatars/contexts/AvatarContext'
import { useFriendsData } from '../../../../contexts/FriendsDataContext'
import { useGroupDashboardQuery } from '../../../../src/friends/hooks/useGroupsQuery'
import { useGroupChallengesInfiniteQuery } from '../../../../src/challenges/hooks/useChallengesQuery'
import { buildGroupLeaderboard } from '../../../../src/friends/helpers/groupLeaderboard.helper'
import { getUserDisplayName } from '../../../../src/friends/helpers/friendsUser.helper'
import { getSafeDisplayName } from '../../../../src/create/helpers/createUi.helper'
import { HEADER_FLAVORS } from '../../../../components/layout/constants/pageHeader.constants'
import {
  GROUP_LEADERBOARD_YOU_LABEL,
  GROUP_INVITATION_SENT_MESSAGE,
  GROUP_NAME_EDIT,
} from '../../../../src/friends/constants/groups.constants'
import { GROUP_NAME_MAX_LENGTH } from '../../../../src/friends/constants/groupName'
import type { GroupDashboardResponse } from '../../../../src/friends/types/groups.types'
import { useAnalytics } from '../../../../src/analytics/useAnalytics'
import { ANALYTICS_EVENTS } from '../../../../src/analytics'

export default function GroupLeaderboardScreen() {
  const { groupId } = useLocalSearchParams<{ groupId: string }>()
  const router = useRouter()
  const { isLoaded, isSignedIn, isAnonymous, userId, user } = useSupabaseAuth()
  const { currentAvatarId, currentAvatarConfig } = useAvatarContext()
  const theme = useTheme()
  const friendsPageStyles = useMemo(() => getFriendsPageStyles(theme), [theme])
  const styles = useMemo(() => getGroupLeaderboardStyles(theme), [theme])

  const queryClient = useQueryClient()
  const { showSnackbar } = useSnackbar()
  const validGroupId = groupId && typeof groupId === 'string' ? groupId : ''
  const { friends } = useFriendsData()
  const {
    data: dashboard,
    isLoading: loading,
    isError,
    refetch: refetchDashboard,
  } = useGroupDashboardQuery(validGroupId, !!validGroupId && !!userId)
  const group = useMemo(() => dashboardToGroup(dashboard), [dashboard])
  const groupMembers = useMemo(() => dashboard?.members ?? [], [dashboard])

  const {
    data: challengesData,
    fetchNextPage: fetchNextChallengesPage,
    hasNextPage: hasNextChallengesPage,
    isFetchingNextPage: isFetchingNextChallengesPage,
  } = useGroupChallengesInfiniteQuery(validGroupId, !!validGroupId && !!userId)
  const challenges = useMemo(
    () => challengesData?.pages.flatMap((p) => p.data) ?? [],
    [challengesData?.pages]
  )

  const memberNames = useMemo(() => {
    const acc: Record<string, string> = {}
    friends.forEach((f) => {
      acc[f.id] = getSafeDisplayName(f.name, f.email)
    })
    groupMembers.forEach((m) => {
      if (m.name) acc[m.id] = m.name
    })
    return acc
  }, [friends, groupMembers])
  const pendingMemberIds = useMemo(
    () => new Set(friends.filter((f) => f.friendshipStatus === 'pending').map((f) => f.id)),
    [friends]
  )
  const memberAvatars = useMemo(
    () =>
      friends.reduce<
        Record<string, { avatarId?: string | null; avatarConfig?: typeof friends[number]['avatarConfig']; avatar?: string }>
      >((acc, f) => {
        acc[f.id] = { avatarId: f.avatarId ?? null, avatarConfig: f.avatarConfig ?? null, avatar: f.avatar }
        return acc
      }, {}),
    [friends]
  )

  const entries = useMemo(() => {
    if (!group) return []
    return buildGroupLeaderboard(group, challenges, {
      memberNames,
      pendingMemberIds,
      memberAvatars,
    })
  }, [group, challenges, memberNames, pendingMemberIds, memberAvatars])

  const myDisplayName = useMemo(
    () => getUserDisplayName(user) ?? GROUP_LEADERBOARD_YOU_LABEL,
    [user]
  )

  const friendStatusByUserId = useMemo(
    () => buildFriendStatusByUserId(friends),
    [friends]
  )

  useEffect(() => {
    if (!isLoaded) return
    if (!isSignedIn || !userId) {
      router.replace('/sign-in')
      return
    }
    if (!validGroupId) router.replace('/app/friends')
  }, [isLoaded, isSignedIn, userId, validGroupId, router])

  const handleBack = () => router.replace('/app/friends?tab=groups')
  const [showInviteModal, setShowInviteModal] = useState(false)
  const { track } = useAnalytics()
  const handleInvite = useCallback(() => {
    track(ANALYTICS_EVENTS.INVITE_MODAL_OPENED, { source: 'group' })
    setShowInviteModal(true)
  }, [track])

  const isCreator = !!group && !!userId && group.creatorId === userId

  const {
    displayGroupName,
    isEditingGroupName,
    draftGroupName,
    titleBlockWidth,
    pendingSaveRef,
    onTitleLayout,
    handleStartEditGroupName,
    handleBlurGroupName,
    handleGroupNameChange,
    handleSaveGroupName,
  } = useGroupNameEdit(group)

  const { handleLeaveGroup, handleRemoveMember, isRemoveMemberBusy } =
    useGroupMemberActions(group)

  const { handleAddFriend, sendingUserId: friendRequestSendingUserId } = useAddFriendFromMenu()

  const handleCloseInviteModal = useCallback(() => setShowInviteModal(false), [])
  const handleInviteSuccess = useCallback(
    (invitedUserIds: string[]) => {
      setShowInviteModal(false)
      showSnackbar(GROUP_INVITATION_SENT_MESSAGE, 'success')
      if (invitedUserIds.length === 0) return
      const friendById = new Map(friends.map((f) => [f.id, f]))
      const newPending = invitedUserIds.map((id) => {
        const f = friendById.get(id)
        return {
          id,
          name: f ? getSafeDisplayName(f.name, f.email) : null,
          username: null,
          avatarId: f?.avatarId ?? null,
        }
      })
      queryClient.setQueryData<GroupDashboardResponse>(
        ['groups', 'dashboard', validGroupId],
        (old) => {
          if (!old) return old
          const existing = old.pendingInvitees ?? []
          const existingIds = new Set(existing.map((p) => p.id))
          const toAdd = newPending.filter((p) => !existingIds.has(p.id))
          return {
            ...old,
            pendingInvitees: [...existing, ...toAdd],
          }
        }
      )
    },
    [queryClient, validGroupId, friends, showSnackbar]
  )
  if (isLoaded && isAnonymous) {
    return <Redirect href="/app/friends" />
  }
  if (!isLoaded || !isSignedIn || !userId || !validGroupId) return null
  if (!group) {
    if (loading) {
      return (
        <View style={friendsPageStyles.container}>
          <PageHeader flavor={HEADER_FLAVORS.FRIENDS} />
          <View style={styles.empty}>
            <ActivityIndicator size="large" color={theme.primary} />
          </View>
        </View>
      )
    }
    if (isError) {
      return (
        <View style={friendsPageStyles.container}>
          <PageHeader flavor={HEADER_FLAVORS.FRIENDS} />
          <View style={styles.empty}>
            <Text style={styles.emptyText}>Something went wrong. Try again.</Text>
            <Pressable
              onPress={() => refetchDashboard()}
              style={({ pressed, hovered }) => [
                styles.retryButton,
                Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                pressed && styles.retryButtonPressed,
                hovered && Platform.OS === 'web' && styles.retryButtonHover,
              ]}
              accessibilityLabel="Retry"
            >
              <Text style={styles.retryButtonText}>Retry</Text>
            </Pressable>
          </View>
        </View>
      )
    }
    return (
      <View style={friendsPageStyles.container}>
        <PageHeader flavor={HEADER_FLAVORS.FRIENDS} />
        <View style={styles.empty}>
          <Text style={styles.emptyText}>Group not found</Text>
        </View>
      </View>
    )
  }

  if (loading) {
    return (
      <View style={friendsPageStyles.container}>
        <PageHeader flavor={HEADER_FLAVORS.FRIENDS} />
        <View style={styles.empty}>
          <ActivityIndicator size="large" color={theme.primary} />
        </View>
      </View>
    )
  }

  const heroGradientColors: [string, string] = [theme.primary, theme.primaryLight]
  const groupImageSource = group.imageUrl
    ? { uri: group.imageUrl }
    : DEFAULT_GROUP_IMAGE_SOURCE

  const heroElement = (
    <LinearGradient colors={heroGradientColors} style={styles.hero}>
      <View style={styles.heroActionsRow}>
        <Pressable
          onPress={handleBack}
          style={({ pressed, hovered }) => [
            styles.backButtonOnHero,
            Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
            pressed && styles.backButtonOnHeroPressed,
            hovered && Platform.OS === 'web' && styles.backButtonOnHeroHover,
          ]}
          accessibilityLabel="Back"
        >
          <ArrowLeft size={24} color={theme.white} />
        </Pressable>
        <View style={styles.headerActions}>
          <InviteFriendsButton label="Invite" onPress={handleInvite} variant="light" />
        </View>
      </View>
      <View style={styles.heroContent}>
        <View style={styles.groupImageContainer}>
          <Image
            source={groupImageSource}
            style={styles.groupImage}
            contentFit="cover"
            alt={webImageAltGroupCover(displayGroupName)}
          />
        </View>
        <View style={styles.groupTitleRow}>
          <View style={styles.groupTitleNameWrap} onLayout={onTitleLayout}>
            {isEditingGroupName ? (
              <TextInput
                style={[styles.groupTitleText, styles.groupTitleInput]}
                value={draftGroupName}
                onChangeText={handleGroupNameChange}
                placeholder={GROUP_NAME_EDIT.PLACEHOLDER}
                placeholderTextColor="rgba(255,255,255,0.5)"
                maxLength={GROUP_NAME_MAX_LENGTH}
                autoFocus
                selectTextOnFocus
                returnKeyType="done"
                onBlur={handleBlurGroupName}
                onSubmitEditing={handleSaveGroupName}
                testID="group-name-edit-input"
              />
            ) : (
              <Text style={styles.groupTitleText} numberOfLines={2}>
                {displayGroupName}
              </Text>
            )}
          </View>
          {isCreator && (
            <Pressable
              style={({ pressed, hovered }) => [
                styles.groupTitleIconWrap,
                getGroupTitleIconMarginLeft(titleBlockWidth),
                Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                pressed && styles.groupTitleIconWrapPressed,
                hovered && Platform.OS === 'web' && styles.groupTitleIconWrapHover,
              ]}
              onPressIn={() => {
                if (isEditingGroupName) pendingSaveRef.current = true
              }}
              onPress={
                isEditingGroupName ? handleSaveGroupName : handleStartEditGroupName
              }
              accessibilityLabel={
                isEditingGroupName ? GROUP_NAME_EDIT.SAVE : GROUP_NAME_EDIT.EDIT_LABEL
              }
              testID={isEditingGroupName ? 'group-name-edit-save' : 'group-name-edit-trigger'}
            >
              {isEditingGroupName
                ? <Check size={20} color="rgba(255,255,255,0.9)" />
                : <Pencil size={20} color="rgba(255,255,255,0.9)" />
              }
            </Pressable>
          )}
        </View>
      </View>
    </LinearGradient>
  )

  return (
    <View style={friendsPageStyles.container}>
      <PageHeader flavor={HEADER_FLAVORS.FRIENDS} />
      <GroupInvitationModal
        visible={showInviteModal}
        group={group}
        onClose={handleCloseInviteModal}
        onSuccess={handleInviteSuccess}
      />
      <View style={styles.listWrapper}>
        <GroupLeaderboardList
          entries={entries}
          userId={userId}
          myDisplayName={myDisplayName}
          currentAvatarId={currentAvatarId}
          currentAvatarConfig={currentAvatarConfig}
          creatorId={group.creatorId}
          friendStatusByUserId={friendStatusByUserId}
          friendRequestSendingUserId={friendRequestSendingUserId}
          removeMemberSending={isRemoveMemberBusy}
          onRemoveMember={handleRemoveMember}
          onAddFriend={handleAddFriend}
          challenges={challenges}
          memberNames={memberNames}
          onLeaveGroup={handleLeaveGroup}
          onLoadMore={fetchNextChallengesPage}
          hasNextPage={hasNextChallengesPage ?? false}
          isFetchingNextPage={isFetchingNextChallengesPage}
          listHeaderComponent={heroElement}
        />
      </View>
    </View>
  )
}
